/**
 * Students API functions + TanStack Query hooks.
 *
 * useStudentsList  — page-number paginated (EntityTable / EntityFramework)
 * useStudents      — infinite scroll (legacy / parent portal)
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { api } from '@core/api/client';
import type { components } from '@api/index';
import type { EntityListResult } from '@/components/entity/types';

// Billing summary embedded in student doc — refreshed by billing/entitlement mutations.
export interface StudentBillingSummary {
  payment_status: 'paid' | 'outstanding' | 'none';
  remaining_classes: number | null;
  last_updated: string | null;
}

// Augment the generated type with the new billing_summary field.
type _GeneratedStudentResponse = components['schemas']['StudentResponse'];
export type StudentResponse = _GeneratedStudentResponse & {
  billing_summary?: StudentBillingSummary | null;
};
export type PaginatedStudents = components['schemas']['PaginatedResult_StudentResponse_'];

export interface StudentCreate {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  photo_url?: string | null;
  tags?: string[];
  notes?: string | null;
  branch_id?: string | null;
  status?: 'active' | 'inactive' | 'archived';
  guardians?: GuardianInline[];
}

export interface GuardianInline {
  first_name: string;
  last_name: string;
  email?: string | null;
  phone: string;
  relationship?: 'mother' | 'father' | 'guardian' | 'other';
  is_primary?: boolean;
}

export interface StudentUpdate {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  photo_url?: string | null;
  tags?: string[];
  notes?: string | null;
  branch_id?: string | null;
  status?: 'active' | 'inactive' | 'archived';
}

export interface ListStudentsPagedParams {
  status?: string;
  branch?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

export interface ListStudentsParams {
  status?: string;
  branch?: string;
  search?: string;
  limit?: number;
}

export const studentKeys = {
  all: ['students'] as const,
  list: (params: ListStudentsParams) => ['students', 'list', params] as const,
  pagedList: (params: ListStudentsPagedParams) => ['students', 'paged', params] as const,
  detail: (id: string) => ['students', 'detail', id] as const,
};

export async function listStudentsPaged(
  params: ListStudentsPagedParams,
): Promise<EntityListResult<StudentResponse>> {
  return api.get<EntityListResult<StudentResponse>>('/v1/students', {
    query: {
      status: params.status || undefined,
      branch: params.branch || undefined,
      search: params.search || undefined,
      sort: params.sort || undefined,
      order: params.order || undefined,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  });
}

export async function listStudents(
  params: ListStudentsParams & { cursor?: string },
): Promise<PaginatedStudents> {
  return api.get<PaginatedStudents>('/v1/students', {
    query: {
      status: params.status || undefined,
      branch: params.branch || undefined,
      search: params.search || undefined,
      cursor: params.cursor || undefined,
      limit: params.limit ?? 20,
    },
  });
}

export async function getStudent(id: string): Promise<StudentResponse> {
  return api.get<StudentResponse>(`/v1/students/${id}`);
}

export async function createStudent(body: StudentCreate): Promise<StudentResponse> {
  return api.post<StudentResponse>('/v1/students', body);
}

export async function updateStudent(id: string, body: StudentUpdate): Promise<StudentResponse> {
  return api.patch<StudentResponse>(`/v1/students/${id}`, body);
}

export async function deleteStudent(id: string): Promise<void> {
  return api.delete<void>(`/v1/students/${id}`);
}

export async function linkGuardian(studentId: string, guardianId: string): Promise<StudentResponse> {
  return api.post<StudentResponse>(`/v1/students/${studentId}/guardians/${guardianId}`);
}

export async function unlinkGuardian(studentId: string, guardianId: string): Promise<StudentResponse> {
  return api.delete<StudentResponse>(`/v1/students/${studentId}/guardians/${guardianId}`);
}

export function useStudentsList(params: ListStudentsPagedParams) {
  return useQuery({
    queryKey: studentKeys.pagedList(params),
    queryFn: () => listStudentsPaged(params),
    placeholderData: (prev) => prev,
  });
}

export function useStudents(params: ListStudentsParams) {
  return useInfiniteQuery<
    PaginatedStudents,
    Error,
    InfiniteData<PaginatedStudents>,
    ReturnType<typeof studentKeys.list>,
    string | undefined
  >({
    queryKey: studentKeys.list(params),
    queryFn: ({ pageParam }) => listStudents({ ...params, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

/** staleTime:0 — always re-fetch to fix edit pre-load stale-cache bug. */
export function useStudent(id: string) {
  return useQuery({
    queryKey: studentKeys.detail(id),
    queryFn: () => getStudent(id),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createStudent,
    onSuccess: () => { qc.invalidateQueries({ queryKey: studentKeys.all }); },
  });
}

export function useUpdateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StudentUpdate }) => updateStudent(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: studentKeys.all });
      qc.invalidateQueries({ queryKey: studentKeys.detail(id) });
    },
  });
}

export function useDeleteStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteStudent,
    onSuccess: () => { qc.invalidateQueries({ queryKey: studentKeys.all }); },
  });
}

export function useLinkGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, guardianId }: { studentId: string; guardianId: string }) =>
      linkGuardian(studentId, guardianId),
    onSuccess: (_data, { studentId }) => {
      qc.invalidateQueries({ queryKey: studentKeys.all });
      qc.invalidateQueries({ queryKey: studentKeys.detail(studentId) });
      qc.invalidateQueries({ queryKey: ['guardians'] });
    },
  });
}

export function useUnlinkGuardian() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, guardianId }: { studentId: string; guardianId: string }) =>
      unlinkGuardian(studentId, guardianId),
    onSuccess: (_data, { studentId }) => {
      qc.invalidateQueries({ queryKey: studentKeys.all });
      qc.invalidateQueries({ queryKey: studentKeys.detail(studentId) });
      qc.invalidateQueries({ queryKey: ['guardians'] });
    },
  });
}
